/**
 * Demo API Client
 *
 * This client is specifically for the demo app's Next.js backend,
 * which runs alongside the orchestrator and provides similar APIs
 * for demo-specific functionality.
 */

import { createApi } from "@perceptron/api/kyClient";
import { handleSSEStream, type SSEMessage } from "@perceptron/api/sseUtils";
import z from "zod";

import { DEMO_API_PREFIX } from "@demo/constants/routing";
import type { DetectRequest, DetectResponse } from "@demo/server/api/contracts/detect";
import {
  RecoverTrackResponseSchema,
  type RecoverTrackRequest,
  type RecoverTrackResponse,
} from "@demo/server/api/contracts/recover-track";
import { RunOutputPart, RunUsagePart } from "@demo/server/api/contracts/run";
import type {
  S3BatchUploadRequest,
  S3BatchUploadResponse,
  S3BatchDownloadRequest,
  S3BatchDownloadResponse,
} from "@demo/server/api/contracts/s3Presigned";
import { isToolCallEventName, parseToolCallEvent, type ToolCallEvent } from "@demo/server/api/contracts/toolCall";
import { ModelSchema, OutputFormatSchema, type ToolFamily } from "@demo/shared/types";
import { DocumentInputContentItem } from "@demo/types/documentContent";
import {
  RunEventType,
  type RunDefaultChunk,
  type RunUsageChunk,
  type RunErrorChunk,
  RunEventData,
} from "@demo/types/runStreamChunk";

const getBaseUrl = (): string => {
  if (typeof window !== "undefined") return window.location.origin;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
};

// Create demo-specific API instance
const demoApi = createApi({
  baseUrl: getBaseUrl() + DEMO_API_PREFIX,
  retry: 0,
  timeout: 1800000, // 30 minutes (1,800,000 milliseconds) - for long-running streaming responses
  hooks: {
    beforeError: [
      async (error) => {
        const { response } = error;
        if (response) {
          try {
            const body = await response.clone().text();
            if (body) {
              const errorJson = JSON.parse(body);
              // Update the error message with the parsed message
              error.message = errorJson.error || error.message;
            }
          } catch {
            // If parsing fails, keep original error
          }
        }
        return error;
      },
    ],
  },
});

/**
 * S3 batch operations for demo backend
 */
export const demoS3Api = {
  /**
   * Generate batch upload URLs for S3
   */
  batchGenerateUploadUrls: async (request: S3BatchUploadRequest): Promise<S3BatchUploadResponse> => {
    return await demoApi.post<S3BatchUploadRequest, S3BatchUploadResponse>("rest/s3/presignedUrls/upload", request);
  },

  /**
   * Generate batch download URLs for S3
   */
  batchGenerateDownloadUrls: async (request: S3BatchDownloadRequest): Promise<S3BatchDownloadResponse> => {
    return await demoApi.post<S3BatchDownloadRequest, S3BatchDownloadResponse>(
      "rest/s3/presignedUrls/download",
      request,
    );
  },
};

export type StreamRunRequest = {
  model: z.infer<typeof ModelSchema>;
  sessionUuid: string;
  runUuid: string;
  content: DocumentInputContentItem[];
  outputFormatSchema: z.infer<typeof OutputFormatSchema.shape.value>;
  enableReasoning?: boolean;
  /** The tool families to turn on (`focus`, `search`). */
  tools?: ToolFamily[];
  /** Dev-only: the dev fixture to replay instead of calling the model or the harness. */
  fixture?: string;
  /** Dev-only: `slow` stretches a fixture replay's streaming states. */
  fixtureSpeed?: "normal" | "slow";
  enableAudioInVideo?: boolean;
  temperature?: number;
};

/**
 * Model API for demo backend
 */
export const demoModelApi = {
  /** Separate, non-streaming model-assisted tracking request; shares retry: 0. */
  recoverTrack: async (request: RecoverTrackRequest, signal: AbortSignal): Promise<RecoverTrackResponse> => {
    const response = await demoApi.post<RecoverTrackRequest, RecoverTrackResponse>(
      "rest/model/recover-track",
      request,
      signal,
    );
    return RecoverTrackResponseSchema.parse(response);
  },
  /**
   * Stream model run using Server-Sent Events
   * @param request - The model request containing session, content, and output format
   * @param onMessage - Callback for each streamed message part
   * @param onError - Callback for error events
   * @param onUsage - Callback for usage data
   * @param signal - Optional AbortSignal for cancellation
   * @param onToolCallEvent - Callback for each `tool_call_*` event (started, link, completed, failed)
   */
  streamRun: async (
    request: StreamRunRequest,
    onMessage: (part: RunOutputPart) => void,
    onError?: (error: string) => void,
    onUsage?: (usage: RunUsagePart) => void,
    signal?: AbortSignal,
    onToolCallEvent?: (event: ToolCallEvent) => void,
  ): Promise<void> => {
    const response = await demoApi.stream(`rest/model/stream`, request, signal);

    if (!response.ok) {
      throw new Error(`Stream failed: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();

    // Use the SSE helper to handle the stream
    await handleSSEStream({
      reader,
      onEvent: (message: SSEMessage): void => {
        // Handle different event types
        switch (message.event) {
          case undefined: {
            // Default event - normal content data
            const defaultData = tryParseSSEEvent<RunDefaultChunk>(message.data);
            if (defaultData) {
              const part: RunOutputPart = {
                content: defaultData.content,
                reasoning_content: defaultData.reasoning_content,
                finish_reason: defaultData.finish_reason,
                timestamp: defaultData.timestamp,
              };
              onMessage(part);
            }
            return;
          }
          case RunEventType.USAGE: {
            // Token usage event
            const usageData = tryParseSSEEvent<RunUsageChunk>(message.data);
            if (usageData && onUsage) {
              onUsage({
                inputTokens: usageData.usage.prompt_tokens,
                outputTokens: usageData.usage.completion_tokens,
                totalTokens: usageData.usage.total_tokens,
              });
            }
            return;
          }
          case RunEventType.ERROR: {
            // Error event from server
            const errorData = tryParseSSEEvent<RunErrorChunk>(message.data);
            if (errorData) {
              if (onError) {
                onError(errorData.error);
              } else {
                console.error("SSE Error event:", errorData.error);
              }
              reader.cancel();
            }
            return;
          }
          default: {
            if (isToolCallEventName(message.event)) {
              // Tool-call events are additive and fail soft: a frame that fails its schema is
              // logged and dropped, and the text stream is unaffected.
              const raw = tryParseSSEEvent<RunEventData>(message.data);
              const event = raw ? parseToolCallEvent(message.event, raw) : null;
              if (!event) {
                console.warn("Dropped malformed tool-call frame:", message.event, message.data);
              } else if (onToolCallEvent) {
                onToolCallEvent(event);
              }
              return;
            }
            // An unrecognised event name is logged and ignored.
            console.warn("Unknown SSE event type:", message.event);
          }
        }
      },
      onError: (error) => {
        console.error("SSE parser error:", error);
        if (onError) {
          onError(error.message);
        }
      },
      signal,
    });
  },
};

/**
 * Object detection via the demo backend proxy (`POST /demo/api/rest/detect`).
 * Rejects with a ky `HTTPError` on non-2xx — the route's `error` field becomes
 * `error.message`, and the HTTP status stays on `error.response.status`.
 */
export const demoDetectApi = {
  detect: async (request: DetectRequest, signal?: AbortSignal): Promise<DetectResponse> => {
    return await demoApi.post<DetectRequest, DetectResponse>("rest/detect", request, signal);
  },
};

const tryParseSSEEvent = <T extends RunEventData>(data: string): T | null => {
  try {
    return JSON.parse(data) as T;
  } catch (e) {
    console.error("Failed to parse SSE data:", e, "Raw data:", data);
    return null;
  }
};
